import { productClusterList } from "store/ai-cluster/AiClusterType";
import { ProductSequenceData } from "../store/product-list/ProductListTypes";
import { SmartSortValues, SmartSortingRulesList } from "./Constants";
import { Product } from "../store/product/ProductTypes";
import { isListPriceGreaterThanPrice } from "./ProductUtil";
import { SMART_SORT_RULE_IDS } from "./Constants";

interface EnhancedProductData extends ProductSequenceData {
  cluster_score: number;
  rank: string;
  stock: number | null;
  launchDate: Date | null;
}

const createSimilarityMap = (
  productSimilarity: productClusterList[],
): Map<string, productClusterList> => {
  return new Map(productSimilarity.map((item) => [item.productId, item]));
};

const enhanceProduct = (
  product: ProductSequenceData,
  similarityData: productClusterList | undefined,
  productStock: number | null,
  productLaunchDate: Date | null,
): EnhancedProductData => {
  return {
    ...product,
    cluster_score: similarityData?.cluster_score ?? 0,
    rank: similarityData?.rank ?? "neutral",
    stock: productStock,
    launchDate: productLaunchDate,
  };
};

const partitionAndSortProducts = (
  products: EnhancedProductData[],
  filterCondition: (product: EnhancedProductData) => boolean,
  isReversed: boolean = false,
  sortByDate: boolean = false,
  customSortFn?: (a: EnhancedProductData, b: EnhancedProductData) => number,
): ProductSequenceData[] => {
  const [matchingProducts, otherProducts] = products.reduce<
    [EnhancedProductData[], EnhancedProductData[]]
  >(
    ([matching, others], product) => {
      return filterCondition(product)
        ? [[...matching, product], others]
        : [matching, [...others, product]];
    },
    [[], []],
  );

  if (customSortFn) {
    matchingProducts.sort(customSortFn);
  } else if (sortByDate) {
    matchingProducts.sort((a, b) => {
      const dateA = a.launchDate ? new Date(a.launchDate).getTime() : 0;
      const dateB = b.launchDate ? new Date(b.launchDate).getTime() : 0;
      return dateB - dateA;
    });
  } else {
    matchingProducts.sort((a, b) => b.cluster_score - a.cluster_score);
  }

  return isReversed
    ? [...otherProducts, ...matchingProducts]
    : [...matchingProducts, ...otherProducts];
};

const isNewProduct = (product: EnhancedProductData): boolean => {
  if (!product.launchDate) return false;
  const launchDate = new Date(product.launchDate);
  const currentDate = new Date();
  const diffTime = Math.abs(currentDate.getTime() - launchDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 30;
};

const isDiscountedProduct = (
  product: EnhancedProductData,
  allProducts: { [key: string]: Product },
): boolean => {
  const productData = allProducts[product.productId];
  if (!productData) return false;

  const listPrice = productData.listPrice?.toString() || null;
  const lowPrice = productData.lowPrice;
  const highPrice = productData.highPrice;

  let priceRange: number[] | null = null;
  if (lowPrice !== undefined && highPrice !== undefined) {
    priceRange = [lowPrice, highPrice];
  } else if (lowPrice !== undefined) {
    priceRange = [lowPrice];
  }

  return isListPriceGreaterThanPrice(listPrice, priceRange);
};

const sortDiscountedProducts = (
  productsWithScores: EnhancedProductData[],
  allProducts: { [key: string]: Product },
  isReversed: boolean = true,
): ProductSequenceData[] => {
  const discountedProductPrices = new Map<string, number>();

  productsWithScores.forEach((product) => {
    if (isDiscountedProduct(product, allProducts)) {
      const productData = allProducts[product.productId];
      const lowPrice = productData.lowPrice;
      const highPrice = productData.highPrice;

      const discountedPrice =
        lowPrice !== undefined
          ? lowPrice
          : highPrice !== undefined
            ? highPrice
            : 0;
      discountedProductPrices.set(product.productId, discountedPrice);
    }
  });

  return partitionAndSortProducts(
    productsWithScores,
    (product) => discountedProductPrices.has(product.productId),
    isReversed,
    false,
    (a, b) =>
      (discountedProductPrices.get(b.productId) || 0) -
      (discountedProductPrices.get(a.productId) || 0),
  );
};

// Get Updated Product List
export const getUpdatedProductList = (
  products: ProductSequenceData[],
  productSimilarity: productClusterList[],
  allProducts: { [key: string]: Product },
  sortType: string,
): {
  updatedProductList: ProductSequenceData[];
  promotedProducts?: string[];
  demotedProducts?: string[];
} => {
  const similarityMap = createSimilarityMap(productSimilarity);
  const productsWithScores: EnhancedProductData[] = products.map((product) =>
    enhanceProduct(
      product,
      similarityMap.get(product.productId),
      allProducts[product.productId]?.stock ?? null,
      allProducts[product.productId]?.launchDate ?? null,
    ),
  );
  let updatedProductList: ProductSequenceData[] = [...products];
  let promotedProducts: string[] = [];
  let demotedProducts: string[] = [];

  // sorting based on sortType
  switch (sortType) {
    case SmartSortValues.boost:
      // Get boosted product IDs before sorting
      promotedProducts = productsWithScores
        .filter((product) => product.rank === SmartSortValues.boost)
        .map((product) => product.productId);

      updatedProductList = partitionAndSortProducts(
        productsWithScores,
        (product) => product.rank === SmartSortValues.boost,
      );
      return { updatedProductList, promotedProducts };

    case SmartSortValues.bury:
      // Get buried product IDs before sorting
      demotedProducts = productsWithScores
        .filter((product) => product.rank === SmartSortValues.bury)
        .map((product) => product.productId);

      updatedProductList = partitionAndSortProducts(
        productsWithScores,
        (product) => product.rank === SmartSortValues.bury,
        true,
      );
      return { updatedProductList, demotedProducts };
    case SmartSortValues.outOfStock:
      // Get out of stock product IDs before sorting
      demotedProducts = productsWithScores
        .filter((product) => product.stock === 0)
        .map((product) => product.productId);

      updatedProductList = partitionAndSortProducts(
        productsWithScores,
        (product) => product.stock === 0,
        true,
      );
      return { updatedProductList, demotedProducts };
    case SmartSortValues.newProducts:
      // Get new product IDs before sorting
      promotedProducts = productsWithScores
        .filter((product) => isNewProduct(product))
        .map((product) => product.productId);

      updatedProductList = partitionAndSortProducts(
        productsWithScores,
        (product) => isNewProduct(product),
        false,
        true,
      );
      return { updatedProductList, promotedProducts };
    case SmartSortValues.discountedProductsToTop:
      promotedProducts = productsWithScores
        .filter((product) => isDiscountedProduct(product, allProducts))
        .map((product) => product.productId);

      updatedProductList = sortDiscountedProducts(
        productsWithScores,
        allProducts,
        false,
      );
      return { updatedProductList, promotedProducts };
    case SmartSortValues.discountedProductsToBottom:
      // Get discounted product IDs using the reusable helper function
      productsWithScores.forEach((product) => {
        if (isDiscountedProduct(product, allProducts)) {
          demotedProducts.push(product.productId);
        }
      });

      updatedProductList = sortDiscountedProducts(
        productsWithScores,
        allProducts,
      );
      return { updatedProductList, demotedProducts };

    default:
      return { updatedProductList, promotedProducts, demotedProducts };
  }
};

/**
 * Checks if a rule ID is a smart sort rule ID (hardcoded rule IDs)
 * @param ruleId - The rule ID to check
 * @returns true if the rule ID is a smart sort rule ID
 */
export const isSmartSortRuleId = (ruleId: string): boolean => {
  return SmartSortingRulesList.some((smartRule) => smartRule.value === ruleId);
};

/**
 * Determines if a smart sort rule is a "move up" rule (promotes products) or "move down" rule (demotes products)
 * @param ruleId - The rule ID to check
 * @returns true if it's a move up rule, false if it's a move down rule
 */
export const isSmartSortPromoteRule = (ruleId: string): boolean => {
  return (
    ruleId === SMART_SORT_RULE_IDS.NEW || ruleId === SMART_SORT_RULE_IDS.BOOSTED || ruleId === SMART_SORT_RULE_IDS.DISCOUNTED_TO_TOP
  );
};
